import { ConnectionPlanCard } from '../connections/ConnectionPlanCard';
import { ConnectionSetupCard } from '../connections/ConnectionSetupCard';
import { GmailSetupCard } from '../email/GmailConnection';
import { ConnectionInstructionsCard, type InstructionsProposal } from '../connections/ConnectionInstructionsCard';
import { ConnectionOperationCard } from '../connections/ConnectionOperationCard';
import { IntegrationGroup } from '../integrations/IntegrationGroup';
import { CalendarCard } from '../integrations/CalendarCards';
import { useState } from 'react';
import type { AgentPart, ChatMessage } from '../../lib/api-types';
import {
  isSafeMarkdownUrl,
  MarkdownContent,
  MarkdownModeToggle,
  MarkdownSource,
  type MarkdownDisplayMode,
} from '../content/MarkdownContent';
import { Icon } from '../ui/Icon';
import { ChatEmailCard } from '../email/Emails';
import styles from './Chat.module.css';
import { ArtifactCard } from './ArtifactCard';
import { LearningNotice } from './LearningNotice';
import { artifactKey, artifactReference } from './artifact-reference';

/** Turns a tool name into something a person reads without decoding. */
function toolLabel(name: string | undefined): string {
  if (!name) return 'Working';
  const cleaned = name.replace(/^mcp__[^_]+__/, '').replace(/[_-]+/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Shown while a turn is streaming but has rendered nothing yet. On the
 * 5-series the model thinks before emitting its first token, so the gap
 * between sending and the first visible output can run several seconds.
 */
export function ThinkingIndicator() {
  return (
    <div className={styles.thinking} role="status" aria-label="Thinking">
      <span aria-hidden="true">Thinking</span>
      <span className={styles.thinkingDot} aria-hidden="true">
        .
      </span>
      <span className={styles.thinkingDot} aria-hidden="true">
        .
      </span>
      <span className={styles.thinkingDot} aria-hidden="true">
        .
      </span>
    </div>
  );
}

export function PartsView({
  parts,
  streaming,
}: {
  parts: readonly AgentPart[];
  streaming?: boolean;
}) {
  const [markdownMode, setMarkdownMode] =
    useState<MarkdownDisplayMode>('preview');
  const groupProvider = (part: AgentPart) => {
    const output = part.output as
      | {
          integrationGroup?: { provider?: string };
          calendarCard?: unknown;
          emailCard?: unknown;
        }
      | undefined;
    if (output?.integrationGroup) return output.integrationGroup.provider;
    if (part.toolName === 'get_calendar_events' && output?.calendarCard)
      return 'google_calendar';
    if (part.toolName === 'get_email_review_queue' && output?.emailCard)
      return 'gmail';
    return undefined;
  };
  const grouped = parts
    .filter((part) => part.type === 'tool_result' && groupProvider(part))
    .sort(
      (a, b) =>
        Number(groupProvider(a) === 'gmail') -
        Number(groupProvider(b) === 'gmail'),
    );
  let groupIndex = 0;
  const orderedParts = parts.map((part) =>
    part.type === 'tool_result' && groupProvider(part)
      ? grouped[groupIndex++]!
      : part,
  );

  const resultByToolId = new Map(
    parts
      .filter((part) => part.type === 'tool_result' && part.toolId)
      .map((part) => [part.toolId as string, part]),
  );
  const callByToolId = new Map(parts.filter(part => part.type === 'tool_call' && part.toolId).map(part => [part.toolId!, part]));
  const artifactByPart = new Map(parts.flatMap(part => {
    const ref = artifactReference(part, part.toolId ? callByToolId.get(part.toolId) : undefined);
    return ref ? [[part, ref] as const] : [];
  }));
  const lastArtifact = new Map([...artifactByPart].map(([part, ref]) => [artifactKey(ref), part]));
  return (
    <>
      {orderedParts.map((part, index) => {
        const key = part.id ?? part.toolId ?? `${part.type}-${index}`;
        const artifact = artifactByPart.get(part);
        if (artifact) return lastArtifact.get(artifactKey(artifact)) === part
          ? <ArtifactCard key={key} reference={artifact} /> : null;
        if (
          part.type === 'tool_result' &&
          !part.error &&
          part.output &&
          typeof part.output === 'object'
        ) {
          const data = part.output as {
            connectionPlan?: {version:number;planId:string};
            connectionSetup?: {version:number;setupId:string};
            gmailSetup?: {version:number};
            connectionInstructions?: InstructionsProposal;
            connectionOperation?: {version?:number;operationId?:string};
            integrationGroup?: { version?: number; resultSetId?: string };
            calendarCard?: { eventId?: string; draftId?: string };
          };
          if(data.gmailSetup?.version===1)return <GmailSetupCard key={key}/>;
          if(data.gmailSetup)return <p key={key}>This Gmail setup needs a newer app version.</p>;
          if(data.connectionSetup?.version===1 && data.connectionSetup.setupId)return <ConnectionSetupCard key={key} setupId={data.connectionSetup.setupId}/>;
          if(data.connectionSetup)return <p key={key}>This connection setup needs a newer app version.</p>;
          if(data.connectionPlan?.version===1)return <ConnectionPlanCard key={key} planId={data.connectionPlan.planId}/>;
          if(data.connectionInstructions?.version===1)return <ConnectionInstructionsCard key={key} proposal={data.connectionInstructions}/>;
          if(data.connectionOperation?.version===1&&data.connectionOperation.operationId)return <ConnectionOperationCard key={key} operationId={data.connectionOperation.operationId} compact/>;
          if(data.connectionOperation&&data.connectionOperation.version!==1)return <p key={key}>This saved connection result needs a newer app version.</p>;
          if (data.integrationGroup && data.integrationGroup.version !== 1)
            return (
              <p key={key}>This saved result needs a newer app version.</p>
            );
          if (
            data.integrationGroup?.version === 1 &&
            data.integrationGroup.resultSetId
          )
            return (
              <IntegrationGroup
                key={key}
                resultSetId={data.integrationGroup.resultSetId}
              />
            );
          if (
            data.calendarCard &&
            (data.calendarCard.eventId || data.calendarCard.draftId)
          )
            return <CalendarCard key={key} {...data.calendarCard} />;
          const card = (part.output as { emailCard?: { threadId?: unknown } })
            .emailCard;
          if (typeof card?.threadId === 'string')
            return <ChatEmailCard key={key} threadId={card.threadId} />;
        }
        if (part.type === 'text') {
          const isLast = index === parts.length - 1;
          return (
            <TextPart
              key={key}
              text={part.text ?? ''}
              showCursor={Boolean(streaming) && isLast}
              mode={markdownMode}
            />
          );
        }
        if (part.type === 'thinking' && part.text) {
          return (
            <div
              key={key}
              className={styles.thinkingText}
              data-streaming={Boolean(streaming)}
            >
              <span className={styles.thinkingLabel}>Thinking</span>
              <span>{part.text}</span>
            </div>
          );
        }
        if (part.type === 'citation' && part.citation) {
          const label = part.citation.title || part.citation.url;
          if (!isSafeMarkdownUrl(part.citation.url)) {
            return (
              <span key={key} className={styles.citation}>
                {label}
              </span>
            );
          }
          return (
            <a
              key={key}
              className={styles.citation}
              href={part.citation.url}
              target="_blank"
              rel="noreferrer"
              title={part.citation.citedText}
            >
              {part.citation.title || part.citation.url}
            </a>
          );
        }
        if (part.type === 'tool_call') {
          const result = part.toolId
            ? resultByToolId.get(part.toolId)
            : undefined;
          if (result && artifactByPart.has(result)) return null;
          const operation = (result?.output as { connectionOperation?: { version?: number; operationId?: string } } | undefined)?.connectionOperation;
          if (!result?.error && operation?.version === 1 && operation.operationId) return null;
          const status = result
            ? result.error
              ? 'failed'
              : 'complete'
            : part.status === 'running' && streaming === false
              ? 'cancelled'
              : (part.status ?? 'complete');
          const running = status === 'running';
          const label = toolLabel(part.toolName);
          return (
            <div
              key={key}
              className={styles.tool}
              data-status={status}
              {...(running
                ? { role: 'status', 'aria-label': `${label} in progress` }
                : {})}
            >
              <span className={styles.toolIcon}>
                <Icon name={status === 'failed' ? 'x' : 'tools'} size={13} />
              </span>
              {label}
              {running ? '…' : status === 'cancelled' ? ' stopped' : ''}
              {status === 'failed' && result?.error
                ? ` failed — ${result.error}`
                : ''}
            </div>
          );
        }
        if (
          part.type === 'tool_result' &&
          part.error &&
          !parts.some(
            (candidate) =>
              candidate.type === 'tool_call' &&
              candidate.toolId === part.toolId,
          )
        ) {
          return (
            <div key={key} className={styles.tool} data-status="failed">
              <span className={styles.toolIcon}>
                <Icon name="x" size={13} />
              </span>
              {toolLabel(part.toolName)} failed — {part.error}
            </div>
          );
        }
        // Successful results are not shown on their own; the assistant's text
        // is the report. Showing raw tool output would be noise.
        return null;
      })}
      {!streaming &&
      parts.some(
        (part) => part.type === 'text' && Boolean(part.text?.trim()),
      ) ? (
        <MarkdownModeToggle
          mode={markdownMode}
          onChange={setMarkdownMode}
          label="Assistant message view"
        />
      ) : null}
    </>
  );
}

function TextPart({
  text,
  showCursor,
  mode,
}: {
  text: string;
  showCursor: boolean;
  mode: MarkdownDisplayMode;
}) {
  return (
    <div className={styles.assistant}>
      {showCursor ? (
        <span className={styles.streamingText}>{text}</span>
      ) : mode === 'preview' ? (
        <MarkdownContent source={text} />
      ) : (
        <MarkdownSource source={text} />
      )}
      {showCursor ? (
        <span className={styles.cursor} aria-hidden="true" />
      ) : null}
    </div>
  );
}

export function MessageView({ message }: { message: ChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className={styles.message} data-role="user">
        <div className={styles.userBubble}>{message.text}</div>
      </div>
    );
  }
  return (
    <div className={styles.message} data-role="assistant">
      <PartsView
        parts={
          message.parts?.length
            ? message.parts
            : [{ type: 'text', text: message.text }]
        }
        streaming={message.status === 'streaming'}
      />
      {message.status === 'streaming' && !message.parts?.length ? (
        <ThinkingIndicator />
      ) : null}
      {message.status === 'cancelled' ? (
        <div className={styles.stopped}>Stopped</div>
      ) : null}
      {message.status !== 'streaming' && message.learningId && <LearningNotice id={message.learningId} />}
    </div>
  );
}
